import React, { useState, useEffect, useMemo } from 'react';
import { Search, Check, Loader2 } from 'lucide-react';
import { procedureApi } from '@features/procedures/services/procedureApi';
import type { ProcedureWithGroupDto } from '@features/procedures/types/procedure';
import type { ClinicService } from '../../../types/wizard';
import '../steps/ServicesStep.css';

interface ServicesFormProps {
  services: ClinicService[];
  onUpdateServices: (services: ClinicService[]) => void;
}

const ServicesForm: React.FC<Readonly<ServicesFormProps>> = ({ services, onUpdateServices }) => {
  const [procedures, setProcedures] = useState<ProcedureWithGroupDto[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    if (procedures.length === 0) {
      loadProcedures();
    }
  }, [procedures.length]);

  const loadProcedures = async () => {
    setIsLoading(true);
    setLoadError(null);
    try {
      // Clinic Network list only (active ProcedureGroupMapping row) — same list as the profile and the matrix page
      const data = await procedureApi.getProcedures(undefined, true);
      setProcedures(data);
    } catch (error) {
      console.error('Failed to load procedures:', error);
      setLoadError('Failed to load services. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const filteredProcedures = useMemo(() => {
    if (!searchTerm.trim()) return procedures;

    const term = searchTerm.toLowerCase();
    return procedures.filter(
      (proc) =>
        proc.effectiveName.toLowerCase().includes(term) ||
        proc.procedureCode.toLowerCase().includes(term) ||
        (proc.groupName && proc.groupName.toLowerCase().includes(term))
    );
  }, [procedures, searchTerm]);

  const groupedProcedures = useMemo(() => {
    const groups: Record<string, ProcedureWithGroupDto[]> = {};

    filteredProcedures.forEach((proc) => {
      const groupName = proc.groupName || 'Uncategorized';
      if (!groups[groupName]) {
        groups[groupName] = [];
      }
      groups[groupName].push(proc);
    });

    const sortedGroups = Object.entries(groups).sort(([a], [b]) => {
      if (a === 'Uncategorized') return 1;
      if (b === 'Uncategorized') return -1;
      return a.localeCompare(b);
    });

    return sortedGroups;
  }, [filteredProcedures]);

  const isServiceSelected = (procedureId: number): boolean => {
    return services.some((s) => s.serviceProcedureId === procedureId);
  };

  const handleToggleService = (procedure: ProcedureWithGroupDto) => {
    const isSelected = isServiceSelected(procedure.procedureId);

    if (isSelected) {
      onUpdateServices(services.filter((s) => s.serviceProcedureId !== procedure.procedureId));
    } else {
      const newService: ClinicService = {
        serviceProcedureId: procedure.procedureId,
        name: procedure.effectiveName,
        code: procedure.procedureCode,
        fee: undefined,
      };
      onUpdateServices([...services, newService]);
    }
  };

  const handleSelectAll = () => {
    const allServices: ClinicService[] = filteredProcedures.map((proc) => ({
      serviceProcedureId: proc.procedureId,
      name: proc.effectiveName,
      code: proc.procedureCode,
      fee: undefined,
    }));
    onUpdateServices(allServices);
  };

  const handleClearAll = () => {
    onUpdateServices([]);
  };

  if (isLoading) {
    return (
      <div className="services-section">
        <div className="services-loading">
          <Loader2 className="spinner" size={24} />
          <span>Loading services...</span>
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="services-section">
        <div className="services-error">
          <p>{loadError}</p>
          <button type="button" className="btn-retry" onClick={loadProcedures}>
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="services-section">
      <div className="services-toolbar">
        <div className="search-box">
          <Search size={18} className="search-icon" />
          <input
            type="text"
            placeholder="Search services by name or code..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            aria-label="Search services"
          />
        </div>
        <div className="selection-actions">
          <button type="button" className="btn-link" onClick={handleSelectAll}>
            Select All
          </button>
          <span className="divider">|</span>
          <button type="button" className="btn-link" onClick={handleClearAll}>
            Clear All
          </button>
        </div>
      </div>

      <div className="selection-summary">
        <span className="selection-count">{services.length}</span> service{services.length !== 1 ? 's' : ''} selected
      </div>

      <div className="services-list">
        {groupedProcedures.length === 0 ? (
          <div className="no-results">
            {searchTerm ? (
              <p>No services found matching "{searchTerm}"</p>
            ) : (
              <p>No services available</p>
            )}
          </div>
        ) : (
          groupedProcedures.map(([groupName, procs]) => (
            <div key={groupName} className="service-group">
              <div className="group-header">
                <span className="group-name">{groupName}</span>
                <span className="group-count">{procs.length}</span>
              </div>
              <div className="group-services">
                {procs.map((proc) => {
                  const isSelected = isServiceSelected(proc.procedureId);
                  return (
                    <div
                      key={proc.procedureId}
                      className={`service-item ${isSelected ? 'selected' : ''}`}
                      onClick={() => handleToggleService(proc)}
                    >
                      <div className={`service-checkbox ${isSelected ? 'checked' : ''}`}>
                        {isSelected && <Check size={14} />}
                      </div>
                      <div className="service-info">
                        <span className="service-name">{proc.effectiveName}</span>
                        <span className="service-code">{proc.procedureCode}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default ServicesForm;
